<template>
  <div>
    <!-- فیلتر بازه‌ی زمانی و استاد؛ برای تسویه‌ی ماهانه -->
    <div class="report-filters">
      <div class="report-presets" role="group" aria-label="بازه‌ی آماده">
        <button
          v-for="preset in presets"
          :key="preset.key"
          type="button"
          class="report-preset"
          :class="{ 'report-preset--active': activePreset === preset.key }"
          @click="applyPreset(preset.key)"
        >
          {{ preset.title }}
        </button>
      </div>
      <div class="report-filters__row">
        <CustomDatePicker
          inputId="supportReportFrom"
          inputLabel="از تاریخ"
          inputPlaceholder="۱۴۰۵/۰۷/۰۱"
          :value="filters.fromDate"
          @getSelectedDate="(date) => setDate('fromDate', date)"
        />
        <CustomDatePicker
          inputId="supportReportTo"
          inputLabel="تا تاریخ"
          inputPlaceholder="۱۴۰۵/۰۷/۳۰"
          :value="filters.toDate"
          @getSelectedDate="(date) => setDate('toDate', date)"
        />
        <!-- فقط اساتیدی که حداقل یک اتصال پشتیبانی داشته‌اند -->
        <b-form-group label="استاد" label-for="supportReportTeacher">
          <b-form-select id="supportReportTeacher" v-model="filters.teacherId" :options="teacherOptions" />
        </b-form-group>
        <b-button variant="primary" class="report-filters__apply" :disabled="isLoading" @click="load">
          اعمال فیلتر
        </b-button>
      </div>
      <p class="report-period">
        <feather-icon icon="CalendarIcon" size="14" />
        بازه‌ی گزارش: <strong>{{ periodLabel }}</strong>
      </p>
    </div>

    <b-overlay :show="isLoading" rounded="lg">
      <!-- قیمت هر ساعت از SupportLesson/GetPrices -->
      <p v-if="prices" class="support-prices">
        <feather-icon icon="TagIcon" size="14" />
        هر ساعت پشتیبانی: زبان‌آموز <strong>{{ formatPrice(prices.studentPricePerHour) }}</strong> تومان ·
        سهم پشتیبان <strong>{{ formatPrice(prices.teacherPricePerHour) }}</strong> تومان
      </p>

      <div class="support-summary">
        <div class="support-summary__card support-summary__card--primary">
          <span>کل زمان رزروشده</span>
          <strong>{{ formatMinutes(summary.totalReservedMinutes) }}</strong>
        </div>
        <div class="support-summary__card support-summary__card--success">
          <span>کل تماس انجام‌شده</span>
          <strong>{{ formatMinutes(summary.totalUsedMinutes) }}</strong>
        </div>
        <div class="support-summary__card support-summary__card--warning">
          <span>کل زمان استفاده‌نشده</span>
          <strong>{{ formatMinutes(summary.totalUnusedMinutes) }}</strong>
        </div>
        <div class="support-summary__card support-summary__card--info">
          <span>مبلغ دریافتی</span>
          <strong>{{ formatPrice(summary.totalCollectedAmount) }} تومان</strong>
        </div>
        <div class="support-summary__card support-summary__card--danger">
          <span>حقوق پشتیبان‌ها</span>
          <strong>{{ formatPrice(summary.totalTeacherSalary) }} تومان</strong>
        </div>
        <div class="support-summary__card support-summary__card--success">
          <span>سود</span>
          <strong>{{ formatPrice(summary.totalProfit) }} تومان</strong>
        </div>
      </div>

      <!-- تسویه: حقوق هر استاد در همین بازه -->
      <div v-if="teachers.length" class="settlement">
        <div class="settlement__head">
          <h5 class="mb-0">تسویه‌ی اساتید در این بازه</h5>
        </div>
        <b-table-simple responsive small class="mb-0">
          <b-thead>
            <b-tr>
              <b-th>استاد</b-th>
              <b-th>زبان‌آموز</b-th>
              <b-th>تماس انجام‌شده</b-th>
              <b-th>حقوق (تومان)</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr v-for="teacher in teachersBySalary" :key="teacher.teacherId">
              <b-td>
                {{ teacher.teacherFullName || `پشتیبان #${teacher.teacherId}` }}
                <small v-if="teacher.teacherMobile" class="d-block text-muted" dir="ltr">{{ teacher.teacherMobile }}</small>
              </b-td>
              <b-td>{{ (teacher.students || []).length }}</b-td>
              <b-td>{{ formatMinutes(teacher.usedMinutes) }}</b-td>
              <b-td class="font-weight-bold">{{ formatPrice(teacher.teacherSalary) }}</b-td>
            </b-tr>
            <b-tr class="settlement__total">
              <b-td>جمع</b-td>
              <b-td>{{ totalStudents }}</b-td>
              <b-td>{{ formatMinutes(summary.totalUsedMinutes) }}</b-td>
              <b-td class="font-weight-bold">{{ formatPrice(summary.totalTeacherSalary) }}</b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </div>

      <div v-if="!isLoading && !teachers.length" class="support-empty">
        <feather-icon icon="BarChart2Icon" size="28" />
        <span>در این بازه تماسی برای اساتید ثبت نشده است.</span>
      </div>

      <div v-for="teacher in teachers" :key="teacher.teacherId" class="support-report">
        <button
          type="button"
          class="support-report__head"
          :aria-expanded="openId === teacher.teacherId ? 'true' : 'false'"
          @click="openId = openId === teacher.teacherId ? null : teacher.teacherId"
        >
          <b-avatar variant="light-primary" size="38" :text="(teacher.teacherFullName || '؟').charAt(0)" />
          <span class="support-report__name">
            {{ teacher.teacherFullName || `پشتیبان #${teacher.teacherId}` }}
            <small v-if="teacher.teacherMobile" dir="ltr">{{ teacher.teacherMobile }}</small>
          </span>
          <span class="support-report__stat">رزروشده <strong>{{ formatMinutes(teacher.reservedMinutes) }}</strong></span>
          <span class="support-report__stat">انجام‌شده <strong class="text-success">{{ formatMinutes(teacher.usedMinutes) }}</strong></span>
          <span class="support-report__stat">استفاده‌نشده <strong class="text-danger">{{ formatMinutes(teacher.unusedMinutes) }}</strong></span>
          <span class="support-report__stat">حقوق <strong>{{ formatPrice(teacher.teacherSalary) }}</strong></span>
          <span class="support-report__stat">زبان‌آموز <strong>{{ (teacher.students || []).length }}</strong></span>
          <feather-icon :icon="openId === teacher.teacherId ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="18" />
        </button>

        <b-table-simple v-if="openId === teacher.teacherId" responsive small class="support-report__table mb-0">
          <b-thead>
            <b-tr>
              <b-th>زبان‌آموز</b-th>
              <b-th>رزروشده</b-th>
              <b-th>انجام‌شده</b-th>
              <b-th>استفاده‌نشده</b-th>
              <b-th>پرداختی (تومان)</b-th>
              <b-th>حقوق پشتیبان</b-th>
              <b-th>سود</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr v-for="student in teacher.students || []" :key="student.studentId">
              <b-td>
                {{ student.studentFullName || `#${student.studentId}` }}
                <small v-if="student.studentMobile" class="d-block text-muted" dir="ltr">{{ student.studentMobile }}</small>
              </b-td>
              <b-td>{{ formatMinutes(student.reservedMinutes) }}</b-td>
              <b-td>{{ formatMinutes(student.usedMinutes) }}</b-td>
              <b-td>{{ formatMinutes(student.unusedMinutes) }}</b-td>
              <b-td>{{ formatPrice(student.paidAmount) }}</b-td>
              <b-td>{{ formatPrice(student.teacherSalary) }}</b-td>
              <b-td class="text-success">{{ formatPrice(student.profit) }}</b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import {
  BOverlay,
  BAvatar,
  BTableSimple,
  BThead,
  BTbody,
  BTr,
  BTh,
  BTd,
  BButton,
  BFormGroup,
  BFormSelect,
} from "bootstrap-vue";
import CustomDatePicker from "@/views/components/Utils/CustomDatePicker.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { GetSupportReport, GetSupportPrices, GetSupportAdminList, SUPPORT_STATUS } from "@/libs/Api/SupportLesson";
import { formatMinutes } from "./supportFormat";

const DAY_MS = 24 * 60 * 60 * 1000;

// روز ماه شمسی یک تاریخ (بدون کتابخانه‌ی جدا)
function jalaliDay(date) {
  return Number(new Intl.DateTimeFormat("en-US-u-ca-persian", { day: "numeric" }).format(date));
}

// اول ماه شمسی
function startOfJalaliMonth(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  while (jalaliDay(d) !== 1) d.setTime(d.getTime() - DAY_MS);
  return d;
}

// تاریخ به شکل YYYY-MM-DD برای API
function toApiDate(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function faDate(value) {
  return new Date(value).toLocaleDateString("fa-IR", { day: "numeric", month: "long", year: "numeric" });
}

const EMPTY_SUMMARY = {
  totalReservedMinutes: 0,
  totalUsedMinutes: 0,
  totalUnusedMinutes: 0,
  totalCollectedAmount: 0,
  totalTeacherSalary: 0,
  totalProfit: 0,
  teachers: [],
};

export default {
  name: "SupportReport",
  components: {
    BOverlay,
    BAvatar,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
    BButton,
    BFormGroup,
    BFormSelect,
    CustomDatePicker,
  },
  data() {
    return {
      isLoading: false,
      summary: { ...EMPTY_SUMMARY },
      prices: null,
      openId: null,
      // پیش‌فرض: ماه شمسی جاری
      filters: { fromDate: null, toDate: null, teacherId: null },
      // اساتیدی که دست‌کم یک درخواست به آن‌ها وصل شده
      connectedTeachers: [],
      activePreset: "thisMonth",
      presets: [
        { key: "thisMonth", title: "این ماه" },
        { key: "lastMonth", title: "ماه قبل" },
        { key: "all", title: "از ابتدا" },
      ],
    };
  },
  computed: {
    teacherOptions() {
      return [
        { value: null, text: "همه‌ی اساتید" },
        ...this.connectedTeachers.map((teacher) => ({ value: teacher.id, text: teacher.name })),
      ];
    },
    // جدول تسویه: بیشترین حقوق بالا
    teachersBySalary() {
      return [...(this.summary.teachers || [])].sort((a, b) => Number(b.teacherSalary) - Number(a.teacherSalary));
    },
    totalStudents() {
      return (this.summary.teachers || []).reduce((sum, teacher) => sum + (teacher.students || []).length, 0);
    },
    periodLabel() {
      const { fromDate, toDate } = this.filters;
      if (!fromDate && !toDate) return "از ابتدا تا امروز";
      return `${fromDate ? faDate(fromDate) : "ابتدا"} تا ${toDate ? faDate(toDate) : "امروز"}`;
    },
    // پرکارترین پشتیبان بالا
    teachers() {
      return [...(this.summary.teachers || [])].sort((a, b) => Number(b.usedMinutes) - Number(a.usedMinutes));
    },
  },
  mounted() {
    this.applyPreset("thisMonth");
    this.loadConnectedTeachers();
  },
  methods: {
    formatMinutes,
    formatPrice(value) {
      return Number(value || 0).toLocaleString("en-US");
    },
    // اساتید فیلتر: از درخواست‌های وصل‌شده (فعال، منقضی یا تمام‌شده)، نه کل لیست اساتید
    async loadConnectedTeachers() {
      const map = new Map();
      const fetchStatus = async (status) => {
        let page = 1;
        let total = 0;
        do {
          const req = new GetSupportAdminList(this);
          req.setRequestParam({ count: 100, pageNumber: page, status });
          // eslint-disable-next-line no-await-in-loop
          await req.get((res) => {
            const data = (res && res.data) || {};
            total = Number(data.total || 0);
            (data.data || []).forEach((row) => {
              if (row.teacherId && !map.has(row.teacherId)) {
                map.set(row.teacherId, String(row.teacherFullName || `استاد #${row.teacherId}`).trim());
              }
            });
          });
          page += 1;
        } while ((page - 1) * 100 < total);
      };
      try {
        await Promise.all([SUPPORT_STATUS.assigned, SUPPORT_STATUS.expired, SUPPORT_STATUS.endTime].map(fetchStatus));
        this.connectedTeachers = [...map]
          .map(([id, name]) => ({ id, name }))
          .sort((a, b) => a.name.localeCompare(b.name, "fa"));
      } catch (error) {
        console.error(error);
      }
    },
    // بازه‌های آماده بر اساس ماه شمسی
    applyPreset(key) {
      this.activePreset = key;
      const today = new Date();
      if (key === "all") {
        this.filters.fromDate = null;
        this.filters.toDate = null;
      } else if (key === "thisMonth") {
        this.filters.fromDate = toApiDate(startOfJalaliMonth(today));
        this.filters.toDate = toApiDate(today);
      } else {
        const thisStart = startOfJalaliMonth(today);
        const lastEnd = new Date(thisStart.getTime() - DAY_MS);
        this.filters.fromDate = toApiDate(startOfJalaliMonth(lastEnd));
        this.filters.toDate = toApiDate(lastEnd);
      }
      this.load();
    },
    setDate(key, date) {
      this.filters[key] = date ? String(date).slice(0, 10) : null;
      this.activePreset = null;
    },
    async load() {
      this.isLoading = true;
      try {
        const report = new GetSupportReport(this);
        const params = {};
        if (this.filters.fromDate) params.fromDate = this.filters.fromDate;
        if (this.filters.toDate) params.toDate = this.filters.toDate;
        if (this.filters.teacherId) params.teacherId = this.filters.teacherId;
        report.setRequestParam(params);
        const prices = new GetSupportPrices(this);
        await Promise.all([
          report.get((res) => {
            const summary = res && res.isSuccess && res.data && res.data.summary;
            this.summary = summary ? { ...EMPTY_SUMMARY, ...summary } : { ...EMPTY_SUMMARY };
          }),
          prices.get((res) => {
            this.prices = res && res.isSuccess ? res.data : null;
          }),
        ]);
      } catch (error) {
        console.error(error);
        this.$toast({
          component: ToastificationContent,
          props: { title: "دریافت گزارش انجام نشد", icon: "AlertTriangleIcon", variant: "danger" },
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.report-filters {
  margin-bottom: 1rem;
  padding: 0.9rem 1rem;
  border-radius: 12px;
  background: #f8f8fb;
}

.report-presets {
  display: inline-flex;
  gap: 0.35rem;
  margin-bottom: 0.75rem;
}

.report-preset {
  padding: 0.35rem 0.9rem;
  border: 1px solid #d8d6de;
  border-radius: 999px;
  background: #fff;
  font-size: 0.85rem;
  color: #5e5873;
}

.report-preset--active {
  border-color: #7367f0;
  background: #7367f0;
  color: #fff;
}

.report-filters__row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr)) auto;
  gap: 0.75rem;
  align-items: end;
}

.report-filters__row .form-group {
  margin-bottom: 0;
}

.report-period {
  margin: 0.6rem 0 0;
  font-size: 0.82rem;
  color: #6e6b7b;
}

.settlement {
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  border: 1px solid rgba(115, 103, 240, 0.25);
  border-radius: 12px;
}

.settlement__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.settlement__total td {
  border-top: 2px solid #ebe9f1;
  font-weight: 700;
}

.dark-layout .report-filters {
  background: #283046;
}

.dark-layout .report-preset {
  border-color: #3b4253;
  background: transparent;
  color: #b4b7bd;
}

@media (max-width: 767.98px) {
  .report-filters__row {
    grid-template-columns: 1fr;
  }
}

.support-prices {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 1rem;
  padding: 8px 12px;
  border-radius: 10px;
  background: #f3f2f7;
  font-size: 0.85rem;
  color: #5e5873;
}

.support-report__name small {
  display: block;
  font-size: 0.75rem;
  font-weight: 400;
  color: #82868b;
}
</style>
<style scoped src="./supportList.css"></style>
<style scoped>
.support-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.support-summary__card {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.9rem 1rem;
  border-radius: 12px;
  border: 1px solid transparent;
  font-size: 0.8rem;
  color: #6e6b7b;
}

.support-summary__card strong {
  font-size: 1.15rem;
  color: #5e5873;
}

.support-summary__card--primary {
  background: rgba(115, 103, 240, 0.08);
  border-color: rgba(115, 103, 240, 0.2);
}

.support-summary__card--success {
  background: rgba(40, 199, 111, 0.08);
  border-color: rgba(40, 199, 111, 0.2);
}

.support-summary__card--warning {
  background: rgba(255, 159, 67, 0.08);
  border-color: rgba(255, 159, 67, 0.2);
}

.support-summary__card--info {
  background: rgba(0, 207, 232, 0.08);
  border-color: rgba(0, 207, 232, 0.2);
}

.support-summary__card--danger {
  background: rgba(234, 84, 85, 0.08);
  border-color: rgba(234, 84, 85, 0.2);
}

.support-report {
  border: 1px solid #ebe9f1;
  border-radius: 12px;
  overflow: hidden;
}

.support-report + .support-report {
  margin-top: 0.75rem;
}

.support-report__head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem 1.25rem;
  width: 100%;
  padding: 0.8rem 1rem;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
}

.support-report__name {
  flex: 1 1 140px;
  font-weight: 700;
}

.support-report__stat {
  font-size: 0.8rem;
  color: #6e6b7b;
  white-space: nowrap;
}

.support-report__table {
  border-top: 1px solid #ebe9f1;
}

.dark-layout .support-summary__card,
.dark-layout .support-report__stat {
  color: #b4b7bd;
}

.dark-layout .support-summary__card strong {
  color: #d0d2d6;
}

.dark-layout .support-report,
.dark-layout .support-report__table {
  border-color: #3b4253;
}

@media (max-width: 991.98px) {
  .support-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
